import clsx from 'clsx'
import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, MapPin, X } from 'lucide-react'
import { TONE_HEX } from '../../../cc/tokens'
import { reportBtnGhost, reportPill } from '../../ui'
import { toApiParams, type AlertFilters } from '../alertFilters'
import { fmtInt, fmtIst, kindLabel, severityTone } from '../alertLabels'
import type { AlertListEnvelope, AlertListRow } from '../alertTypes'
import { useAlertAnalytics } from '../useAlertAnalytics'
import { KindPill, SeverityPill, ViewCard } from './viewKit'

/**
 * "The alerts behind these numbers": the individual alerts under whatever a tab is
 * showing, each one a click away from the trip drawer (map + trip details). Every
 * aggregate tab mounts one, so no tab is a dead end -- a driver, a hotspot, a shift x
 * kind cell or a trend all end in the same place: this alert, on this trip, on the map.
 *
 * It reads the same `/list` the Alert list tab pages through, with the report's filters
 * plus an optional local `scope` (the row the reader clicked in this tab). Fetch on
 * demand only, no polling.
 */

export interface DrillScope {
  /** Human description shown as a removable chip, e.g. "Night · Overspeed". */
  label: string
  /** Extra /list params; they override the report's own filter of the same name. */
  params: Record<string, string>
  onClear: () => void
}

export interface AlertDrillListProps {
  filters: AlertFilters
  setFilters: (patch: Partial<AlertFilters>) => void
  scope?: DrillScope | null
  title?: string
  sub?: string
  /** Shown when nothing is in scope yet, instead of the generic hint. */
  idleHint?: string
  pageSize?: number
}

export const DRILL_PAGE_SIZE = 15

export function AlertDrillList({
  filters, setFilters, scope = null, title, sub, idleHint, pageSize = DRILL_PAGE_SIZE,
}: AlertDrillListProps): JSX.Element {
  const baseKey = useMemo(() => {
    const p = toApiParams(filters)
    for (const [k, v] of Object.entries(scope?.params ?? {})) p.set(k, v)
    p.set('sort', 'ts_desc')
    return p.toString()
  }, [filters, scope])

  const [page, setPage] = useState(0)
  useEffect(() => { setPage(0) }, [baseKey])

  const params = useMemo(() => {
    const p = new URLSearchParams(baseKey)
    p.set('limit', String(pageSize))
    p.set('offset', String(page * pageSize))
    return p
  }, [baseKey, page, pageSize])

  const { rows, envelope, status, error } = useAlertAnalytics<AlertListRow, AlertListEnvelope>('/list', params)
  const total = envelope?.total ?? rows.length
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <ViewCard
      title={title ?? 'Alerts behind these numbers'}
      sub={sub ?? 'Click an alert to see it on the map, inside the trip it happened in.'}
      actions={(
        <>
          {scope ? (
            <span className="inline-flex max-w-[18rem] items-center gap-1 rounded-full bg-blue-50 py-0.5 pl-2.5 pr-1 text-[11.5px] font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
              <span className="truncate" title={scope.label}>{scope.label}</span>
              <button
                type="button"
                onClick={scope.onClear}
                aria-label={`Clear ${scope.label}`}
                className="rounded-full p-0.5 hover:bg-blue-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]"
              >
                <X size={11} />
              </button>
            </span>
          ) : null}
          {status === 'ok' ? <span className="text-[11.5px] tabular-nums text-slate-500">{fmtInt(total)} alerts</span> : null}
        </>
      )}
      bodyClassName="p-0"
    >
      {!scope && idleHint ? <p className="border-b border-slate-100 px-4 py-2 text-[11.5px] text-slate-500">{idleHint}</p> : null}

      {status === 'loading' || status === 'idle' ? (
        <div className="px-4 py-6 text-center text-[12px] text-slate-400" aria-busy="true">Loading alerts…</div>
      ) : status === 'error' ? (
        <div className="px-4 py-6 text-center text-[12px] text-red-600" role="alert">{error ?? 'Could not load alerts'}</div>
      ) : rows.length === 0 ? (
        <div className="px-4 py-6 text-center text-[12px] text-slate-400">No alerts match.</div>
      ) : (
        <ul className="divide-y divide-slate-100" aria-label="Alerts">
          {rows.map((r) => (
            <DrillRow key={r.alert_id} row={r} selected={filters.alert === r.alert_id} onOpen={() => setFilters({ alert: r.alert_id })} />
          ))}
        </ul>
      )}

      {status === 'ok' && total > pageSize ? (
        <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-4 py-2 text-[11.5px] text-slate-500">
          <span className="tabular-nums">
            {fmtInt(page * pageSize + 1)}–{fmtInt(page * pageSize + rows.length)} of {fmtInt(total)}
          </span>
          <div className="flex items-center gap-1.5">
            <button type="button" className={clsx(reportBtnGhost, 'h-7 !px-2')} disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} aria-label="Previous alerts">
              <ChevronLeft size={13} />
            </button>
            <button type="button" className={clsx(reportBtnGhost, 'h-7 !px-2')} disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)} aria-label="Next alerts">
              <ChevronRight size={13} />
            </button>
            <button type="button" className={clsx(reportBtnGhost, 'h-7')} onClick={() => setFilters({ view: 'list' })}>
              Full alert list
            </button>
          </div>
        </div>
      ) : null}
    </ViewCard>
  )
}

function DrillRow({ row, selected, onOpen }: { row: AlertListRow; selected: boolean; onOpen: () => void }): JSX.Element {
  const tone = severityTone(row.severity)
  const where = row.zone ? `${row.place} · ${row.zone}` : row.place
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        aria-current={selected ? 'true' : undefined}
        title={row.message ?? kindLabel(row.kind)}
        className={clsx(
          'group grid w-full grid-cols-[4px_minmax(0,1fr)_auto] items-center gap-3 py-2.5 pl-0 pr-4 text-left transition-colors',
          'hover:bg-slate-50 focus-visible:bg-blue-50/60 focus-visible:outline-none',
          selected && 'bg-blue-50/70',
        )}
      >
        <span className="h-full min-h-[2.25rem] rounded-r" style={{ background: TONE_HEX[tone] }} aria-hidden />
        <span className="min-w-0">
          <span className="flex flex-wrap items-center gap-1.5">
            <SeverityPill severity={row.severity} />
            <KindPill kind={row.kind} />
            <span className="font-mono text-[11.5px] tabular-nums text-slate-500">{fmtIst(row.ts)}</span>
            {row.occurrence_count > 1 ? <span className="text-[11px] text-slate-400">×{fmtInt(row.occurrence_count)}</span> : null}
          </span>
          <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-slate-600">
            <span className="font-medium text-slate-800">{row.vehicle_id ?? 'no vehicle'}</span>
            <span className="text-slate-300">·</span>
            <span className="truncate">{row.driver_label}</span>
            <span className="text-slate-300">·</span>
            <span className="truncate text-slate-500">{where}</span>
          </span>
        </span>
        <span className="flex items-center gap-2">
          {row.trip_id ? (
            <span className={clsx(reportPill('info'), 'hidden sm:inline-flex')} title={`Trip ${row.trip_id}`}>
              {row.origin_hub ?? '?'} → {row.destination_hub ?? '?'}
            </span>
          ) : (
            <span className={clsx(reportPill('neutral'), 'hidden sm:inline-flex')}>no trip</span>
          )}
          <span className="inline-flex items-center gap-1 text-[11.5px] font-medium text-blue-600 group-hover:text-blue-700">
            <MapPin size={13} />
            <span className="hidden md:inline">View on map</span>
          </span>
        </span>
      </button>
    </li>
  )
}
